import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Customer, ClearanceCase, ClearanceDocument, TrainingResult, TrainingSampleSummary,
  DiffAnnotation, DiffReasonType, ItemComparison,
  DescriptionRuleComponent, ItemDescriptionRule,
  TrainingPoolPreviewResponse, NasInboxResponse, NasTicket, NasSubmitItem,
  AdvisorMessage, TrainingBatch, BatchSummary, BatchFailure,
  getCustomers, getCases, getDocuments, uploadDocument, parseDocuments,
  analyzeTrainingPair, getTrainingResult, verifyTrainingSample,
  saveDiffAnnotations, trainingExportUrl, listTrainingSamples,
  getCustomerParseProfile, saveItemDescriptionRule,
  previewTrainingPool, submitTrainingPool,
  listNasInbox, submitNasInbox, saveTrainingNotes,
  chatWithAdvisor, listTrainingBatches,
} from '../services/clearanceService';
import { usePermission } from '../hooks/usePermission';
import { useMessage } from '../contexts/MessageContext';
import { validateSession } from '../utils/sessionValidator';
import FunctionPageHeader from '../components/FunctionPageHeader';
import '../styles/DataTable.css';
import { formatLocal, parseServerDate } from '../utils/dateHelper';

const TRAINING_ELIGIBLE = new Set(['parsed', 'parse_failed', 'reviewing', 'approved', 'xml_ready', 'filed']);

const STATUS_COLORS: Record<string, string> = {
  draft: '#6c757d', docs_uploaded: '#0dcaf0', parsing: '#fd7e14',
  parse_failed: '#dc3545', parsed: '#17a2b8',
  reviewing: '#6610f2', approved: '#198754', xml_ready: '#0d6efd',
  filed: '#20c997', rejected: '#dc3545',
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const ClearanceTrainingCenterPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission, loading: permLoading } = usePermission();
  const { showSuccess, showError, showApiError } = useMessage();
  const hasInit = useRef(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const docPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [activeTab, setActiveTab] = useState<'cases' | 'pool' | 'advisor' | 'batches'>('cases');
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [cases, setCases] = useState<ClearanceCase[]>([]);
  const [samples, setSamples] = useState<TrainingSampleSummary[]>([]);
  const [caseFilter, setCaseFilter] = useState<'all' | 'pending' | 'analyzed'>('all');

  const [expandedCase, setExpandedCase] = useState<number | null>(null);
  const [caseDocs, setCaseDocs] = useState<ClearanceDocument[]>([]);
  const [caseReport, setCaseReport] = useState<TrainingResult | null>(null);
  const [caseLoading, setCaseLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [polling, setPolling] = useState(false);
  const [docPolling, setDocPolling] = useState(false);
  const [uploading, setUploading] = useState(false);

  const reloadSamples = useCallback(async () => {
    const s = await listTrainingSamples();
    setSamples(s);
  }, []);

  useEffect(() => {
    if (!permLoading && !hasInit.current) {
      hasInit.current = true;
      const init = async () => {
        const valid = await validateSession('ClearanceTrainingCenterPage');
        if (!valid) return;
        setLoading(true);
        try {
          const [custs, allCases, allSamples] = await Promise.all([
            getCustomers(), getCases(), listTrainingSamples(),
          ]);
          setCustomers(custs);
          setCases(allCases);
          setSamples(allSamples);
        } catch (err: any) { showApiError(err); }
        setLoading(false);
      };
      init();
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (docPollRef.current) clearInterval(docPollRef.current);
    };
  }, [permLoading, showApiError]); // eslint-disable-line react-hooks/exhaustive-deps

  const startDocPoll = useCallback((caseId: number) => {
    if (docPollRef.current) clearInterval(docPollRef.current);
    setDocPolling(true);
    docPollRef.current = setInterval(async () => {
      try {
        const docs = await getDocuments(caseId);
        setCaseDocs(docs);
        const stillPending = docs.some(d => ['pending', 'parsing', 'running'].includes(d.parse_status));
        if (!stillPending) {
          clearInterval(docPollRef.current!);
          docPollRef.current = null;
          setDocPolling(false);
        }
      } catch {
        clearInterval(docPollRef.current!);
        docPollRef.current = null;
        setDocPolling(false);
      }
    }, 5000);
  }, []);

  const customerMap: Record<number, Customer> = Object.fromEntries(customers.map(c => [c.id, c]));

  const sampleByCaseId: Record<number, TrainingSampleSummary> = {};
  samples.forEach(s => { sampleByCaseId[s.case_id] = s; });

  const [notesOpenCid, setNotesOpenCid] = useState<number | null>(null);
  const [notesText, setNotesText] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);

  const handleSaveNotes = async (cid: number) => {
    setNotesSaving(true);
    try {
      const updated = await saveTrainingNotes(cid, notesText);
      setCustomers(prev => prev.map(c => c.id === cid ? { ...c, training_notes: updated.training_notes } : c));
      showSuccess('SYS020002', { name: '訓練備忘' });
      setNotesOpenCid(null);
    } catch (err: any) { showApiError(err); }
    finally { setNotesSaving(false); }
  };

  const sampleCountByCustomer: Record<number, { total: number; verified: number }> = {};
  samples.forEach(s => {
    if (!sampleCountByCustomer[s.customer_id]) sampleCountByCustomer[s.customer_id] = { total: 0, verified: 0 };
    sampleCountByCustomer[s.customer_id].total++;
    if (s.is_verified) sampleCountByCustomer[s.customer_id].verified++;
  });

  const eligibleCases = cases.filter(c => TRAINING_ELIGIBLE.has(c.status));

  const filteredCases = eligibleCases.filter(c => {
    if (caseFilter === 'pending') return !sampleByCaseId[c.id];
    if (caseFilter === 'analyzed') return !!sampleByCaseId[c.id];
    return true;
  });

  const handleExpandCase = async (caseId: number) => {
    if (expandedCase === caseId) {
      setExpandedCase(null);
      setCaseDocs([]);
      setCaseReport(null);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; setPolling(false); }
      if (docPollRef.current) { clearInterval(docPollRef.current); docPollRef.current = null; setDocPolling(false); }
      return;
    }
    setExpandedCase(caseId);
    setCaseDocs([]);
    setCaseReport(null);
    setAnalyzing(false);
    setPolling(false);
    setDocPolling(false);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (docPollRef.current) { clearInterval(docPollRef.current); docPollRef.current = null; }
    setCaseLoading(true);
    try {
      const docs = await getDocuments(caseId);
      setCaseDocs(docs);
      if (docs.some(d => d.parse_status === 'pending' || d.parse_status === 'parsing')) {
        startDocPoll(caseId);
      }
      const r = await getTrainingResult(caseId);
      setCaseReport(r);
    } catch { /* no report yet */ } // silent ok: training result may not exist yet, non-blocking
    setCaseLoading(false);
  };

  const handleRetryParse = async (caseId: number) => {
    try {
      await parseDocuments(caseId);
      showSuccess('SYS020001', { name: '重新解析' });
      const docs = await getDocuments(caseId);
      setCaseDocs(docs);
      if (docs.some(d => d.parse_status === 'pending' || d.parse_status === 'parsing' || d.parse_status === 'running')) {
        startDocPoll(caseId);
      }
    } catch (err: any) { showApiError(err); }
  };

  const handleUpload = async (caseId: number, file: File) => {
    setUploading(true);
    try {
      await uploadDocument(caseId, file, 'customs_declaration');
      await parseDocuments(caseId);
      showSuccess('SYS020001', { name: '實際申報報單' });
      const docs = await getDocuments(caseId);
      setCaseDocs(docs);
      if (docs.some(d => ['pending', 'parsing', 'running'].includes(d.parse_status))) {
        startDocPoll(caseId);
      }
    } catch (err: any) { showApiError(err); }
    finally { setUploading(false); }
  };

  const handleUploadBundle = async (caseId: number, file: File, docType?: string) => {
    setUploading(true);
    try {
      const created = await uploadDocument(caseId, file, docType);
      const isZip = file.name.toLowerCase().endsWith('.zip');
      const label = isZip ? `${created.length} 份訓練文件（ZIP解壓）` : (docType === 'commercial_invoice' ? '商業發票' : docType === 'packing_list' ? '裝箱單' : 'AWB');
      showSuccess('SYS020001', { name: label });
      await parseDocuments(caseId);
      const docs = await getDocuments(caseId);
      setCaseDocs(docs);
      if (docs.some(d => ['pending', 'parsing', 'running'].includes(d.parse_status))) {
        startDocPoll(caseId);
      }
    } catch (err: any) { showApiError(err); }
    finally { setUploading(false); }
  };

  const handleAnalyze = async (caseId: number) => {
    setAnalyzing(true);
    setCaseReport(null);
    try {
      await analyzeTrainingPair(caseId);
      setPolling(true);
      let attempts = 0;
      pollRef.current = setInterval(async () => {
        attempts++;
        try {
          const r = await getTrainingResult(caseId);
          setCaseReport(r);
          await reloadSamples();
          clearInterval(pollRef.current!);
          pollRef.current = null;
          setPolling(false);
        } catch {
          if (attempts >= 45) {
            clearInterval(pollRef.current!);
            pollRef.current = null;
            setPolling(false);
          }
        }
      }, 4000);
    } catch (err: any) { showApiError(err); }
    finally { setAnalyzing(false); }
  };

  const handleVerify = async (sampleId: number) => {
    try {
      await verifyTrainingSample(sampleId);
      setCaseReport(r => r ? { ...r, is_verified: true } : r);
      await reloadSamples();
      showSuccess('SYS020002', { name: '訓練樣本' });
    } catch (err: any) { showApiError(err); }
  };

  if (permLoading || loading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  if (!hasPermission('ee_clearance_training', 'read')) return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;

  const canUpdate = hasPermission('ee_clearance_training', 'update');

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <FunctionPageHeader funcCode="ee_clearance_training" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
            <img src="/favicon.ico" alt="icon" style={{ height: 16, width: 16 }} />
            <span style={{ fontSize: 12, color: '#6f42c1', fontFamily: 'monospace' }}>
              xcraftforge — 領域 AI 升級引擎 ‧ 50 分鐘 → 5 分鐘
            </span>
          </div>
        </div>
        <a href={trainingExportUrl()} target="_blank" rel="noreferrer"
          style={{ padding: '6px 14px', fontSize: 12, background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 4, color: '#495057', textDecoration: 'none', alignSelf: 'flex-start' }}>
          ⬇ JSONL（全部）
        </a>
      </div>

      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid #e9ecef' }}>
        {([
          { key: 'cases',   label: '訓練案件',   desc: '從案件工作流程產生訓練樣本' },
          { key: 'pool',    label: '訓練池',     desc: '批次餵入歷史申報資料' },
          { key: 'advisor', label: '訓練顧問',   desc: '與 Claude 討論訓練策略與調整方向' },
          { key: 'batches', label: '📊 批次狀態', desc: '查詢訓練批次上傳進度與失敗原因' },
        ] as const).map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            title={tab.desc}
            style={{
              padding: '8px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              background: 'none', border: 'none', borderBottom: activeTab === tab.key ? '3px solid #6f42c1' : '3px solid transparent',
              color: activeTab === tab.key ? '#6f42c1' : '#6c757d',
              marginBottom: -2, transition: 'color 0.15s',
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'pool' && (
        <TrainingPoolTab canUpdate={canUpdate} />
      )}

      {activeTab === 'advisor' && (
        <TrainingAdvisorTab customers={customers} />
      )}

      {activeTab === 'batches' && (
        <BatchStatusTab />
      )}

      {activeTab === 'cases' && <>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: '訓練樣本', value: samples.length, color: '#6f42c1' },
          { label: '已確認', value: samples.filter(s => s.is_verified).length, color: '#198754' },
          { label: '待分析', value: eligibleCases.filter(c => !sampleByCaseId[c.id]).length, color: '#fd7e14' },
          { label: '已學習客戶', value: Object.keys(sampleCountByCustomer).length, color: '#0d6efd' },
        ].map(s => (
          <div key={s.label} style={{ background: '#fff', border: `2px solid ${s.color}`, borderRadius: 8, padding: '10px 16px', minWidth: 100, textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: '#6c757d' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Customer learning cards */}
      {Object.keys(sampleCountByCustomer).length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#495057', marginBottom: 10 }}>客戶學習進度</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {Object.entries(sampleCountByCustomer).map(([cidStr, stats]) => {
              const cid = Number(cidStr);
              const cust = customerMap[cid];
              const verifiedPct = stats.total > 0 ? Math.round((stats.verified / stats.total) * 100) : 0;
              const accuracy = cust?.local_model_accuracy != null ? Number(cust.local_model_accuracy) : null;
              const accColor = accuracy == null ? '#818cf8'
                : accuracy >= 95 ? '#10b981'
                : accuracy >= 85 ? '#6ee7b7'
                : accuracy >= 70 ? '#fbbf24'
                : '#f87171';
              const isNotesOpen = notesOpenCid === cid;
              return (
                <div key={cid} style={{ background: '#1e1b4b', border: '1px solid #312e81', borderRadius: 8, padding: '10px 14px', minWidth: 180, fontFamily: 'monospace', color: '#e2e8f0' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#a78bfa', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                    {cust?.name_zh || `客戶 #${cid}`}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#6ee7b7' }}>{stats.total}</div>
                    {accuracy != null && (
                      <div style={{ fontSize: 18, fontWeight: 700, color: accColor }}>{accuracy.toFixed(1)}%</div>
                    )}
                  </div>
                  <div style={{ fontSize: 10, color: '#818cf8' }}>
                    樣本 ‧ {verifiedPct}% 已確認
                    {accuracy != null && ` ‧ 準確率`}
                  </div>
                  <div style={{ marginTop: 6, height: 3, background: '#312e81', borderRadius: 2 }}>
                    <div style={{ width: `${accuracy ?? verifiedPct}%`, height: '100%', background: accColor, borderRadius: 2 }} />
                  </div>
                  {/* 訓練備忘 */}
                  <button
                    onClick={() => {
                      if (isNotesOpen) { setNotesOpenCid(null); return; }
                      setNotesOpenCid(cid);
                      setNotesText(cust?.training_notes || '');
                    }}
                    style={{ marginTop: 8, width: '100%', padding: '3px 0', fontSize: 10, background: 'transparent', border: '1px solid #4f46e5', color: cust?.training_notes ? '#6ee7b7' : '#818cf8', borderRadius: 3, cursor: 'pointer' }}>
                    {cust?.training_notes ? '✎ 有指示' : '✎ 訓練備忘'} {isNotesOpen ? '▲' : '▼'}
                  </button>
                  {isNotesOpen && (
                    <div style={{ marginTop: 6 }}>
                      <textarea
                        value={notesText}
                        onChange={e => setNotesText(e.target.value)}
                        rows={5}
                        placeholder={'告訴 Claude 這個客戶的申報特殊規則...\n例：HS Code 必須填 11 碼完整格式\n例：品名第35欄格式：P/N:xxx LOT:xxx\n例：net_weight 從 PL 取，不用 Invoice'}
                        style={{ width: '100%', boxSizing: 'border-box', background: '#13111f', border: '1px solid #4f46e5', borderRadius: 3, color: '#e2e8f0', fontSize: 11, padding: '5px 7px', resize: 'vertical', fontFamily: 'monospace' }}
                      />
                      <button onClick={() => handleSaveNotes(cid)} disabled={notesSaving}
                        style={{ marginTop: 4, width: '100%', padding: '4px 0', fontSize: 11, background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer', opacity: notesSaving ? 0.7 : 1 }}>
                        {notesSaving ? '儲存中…' : '💾 儲存 → 下次訓練生效'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Cases table */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#495057' }}>
          訓練案件（共 {filteredCases.length} 件）
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {(['all', 'pending', 'analyzed'] as const).map(f => (
            <button key={f} onClick={() => setCaseFilter(f)}
              style={{
                padding: '4px 12px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
                background: caseFilter === f ? '#6f42c1' : '#f8f9fa',
                color: caseFilter === f ? '#fff' : '#495057',
                border: `1px solid ${caseFilter === f ? '#6f42c1' : '#dee2e6'}`,
              }}>
              {f === 'all' ? '全部' : f === 'pending' ? '待分析' : '已分析'}
            </button>
          ))}
        </div>
      </div>

      <table className="data-table">
        <thead>
          <tr>
            <th>報單號碼</th>
            <th>客戶</th>
            <th>狀態</th>
            <th>訓練狀態</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {filteredCases.length === 0 ? (
            <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24, color: '#6c757d' }}>{t('common.noData')}</td></tr>
          ) : filteredCases.map(c => {
            const sample = sampleByCaseId[c.id];
            const isExpanded = expandedCase === c.id;
            const cust = customerMap[c.customer_id];
            return (
              <React.Fragment key={c.id}>
                <tr style={{ background: isExpanded ? '#f0f4ff' : undefined }}>
                  <td>
                    <button onClick={() => handleExpandCase(c.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0d6efd', fontSize: 13, padding: 0, textAlign: 'left' }}>
                      {isExpanded ? '▼ ' : '▶ '}{c.case_no}
                    </button>
                  </td>
                  <td style={{ fontSize: 12 }}>{cust?.name_zh || `#${c.customer_id}`}</td>
                  <td>
                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, background: STATUS_COLORS[c.status] || '#6c757d', color: '#fff', fontSize: 11, fontWeight: 600 }}>
                      {c.status}
                    </span>
                  </td>
                  <td style={{ fontSize: 11 }}>
                    {sample
                      ? (sample.is_verified
                        ? <span style={{ color: '#198754' }}>✓ 已確認（{sample.field_diffs_count} 差異）</span>
                        : <span style={{ color: '#fd7e14' }}>⏳ 待確認（{sample.field_diffs_count} 差異）</span>)
                      : <span style={{ color: '#6c757d' }}>未分析</span>
                    }
                  </td>
                  <td>
                    <button className="btn-secondary" style={{ padding: '3px 10px', fontSize: 11 }}
                      onClick={() => navigate(`/ee_clearance_cases/${c.id}`)}>
                      報單詳情
                    </button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={5} style={{ background: '#fafafa', padding: 0 }}>
                      {caseLoading ? (
                        <div style={{ padding: 20, color: '#6c757d', fontSize: 13 }}>{t('common.loading')}</div>
                      ) : (
                        <CaseTrainingPanel
                          caseData={c}
                          docs={caseDocs}
                          report={caseReport}
                          analyzing={analyzing}
                          uploading={uploading}
                          polling={polling}
                          docPolling={docPolling}
                          canUpdate={canUpdate}
                          onUploadDecl={(file) => handleUpload(c.id, file)}
                          onUploadBundle={(file, docType) => handleUploadBundle(c.id, file, docType)}
                          onRetryParse={() => handleRetryParse(c.id)}
                          onAnalyze={() => handleAnalyze(c.id)}
                          onVerify={() => caseReport && handleVerify(caseReport.sample_id)}
                        />
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      </>}
    </div>
  );
};

// ─── BatchStatusTab ───────────────────────────────────────────────────────────

const STATUS_META: Record<string, { label: string; color: string; bg: string }> = {
  queued:    { label: '排隊中', color: '#94a3b8', bg: '#1e293b' },
  parsing:   { label: '解析中', color: '#60a5fa', bg: '#0f1929' },
  analyzing: { label: '分析中', color: '#a78bfa', bg: '#1a1330' },
  completed: { label: '已完成', color: '#6ee7b7', bg: '#0a2318' },
  failed:    { label: '失敗',   color: '#f87171', bg: '#2a1500' },
  stalled:   { label: '停滯',   color: '#fb923c', bg: '#2a1f00' },
};

function fmtRelTime(iso: string | null): { rel: string; full: string } {
  if (!iso) return { rel: '—', full: '' };
  const d = parseServerDate(iso) ?? new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const secs = Math.floor(diffMs / 1000);
  const full = d.toLocaleString('zh-TW', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Taipei' });
  if (secs < 60) return { rel: '剛剛', full };
  if (secs < 3600) return { rel: `${Math.floor(secs / 60)} 分鐘前`, full };
  if (secs < 86400) return { rel: `${Math.floor(secs / 3600)} 小時前`, full };
  return { rel: `${Math.floor(secs / 86400)} 天前`, full };
}

function fmtDuration(start: string | null, end: string | null, submitted: string | null): string {
  const s = start ?? submitted;
  if (!s) return '—';
  const fromMs = new Date(s).getTime();
  const toMs = end ? new Date(end).getTime() : Date.now();
  const secs = Math.floor((toMs - fromMs) / 1000);
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m${secs % 60}s`;
}

const BatchExpandDetail: React.FC<{ b: TrainingBatch }> = ({ b }) => {
  const navigate = useNavigate();
  const detailStyle: React.CSSProperties = {
    margin: '6px 10px 10px 28px', padding: '10px 14px',
    borderRadius: 6, fontSize: 12, lineHeight: '1.8',
  };
  if (b.status === 'completed' && b.summary) {
    const s = b.summary;
    return (
      <div style={{ ...detailStyle, background: '#0a2318', border: '1px solid #198754' }}>
        <div style={{ color: '#6ee7b7', fontWeight: 600, marginBottom: 6 }}>✅ 解析成功</div>
        {b.customer_name && (
          <div style={{ color: '#cbd5e1' }}>
            客戶：{b.customer_name}{s.importer_tax_id ? `（統編 ${s.importer_tax_id}）` : ''}
          </div>
        )}
        {b.file_names && (
          <div style={{ color: '#cbd5e1' }}>
            文件：{b.file_names.split(',').map(f => f.trim()).join('、')}
          </div>
        )}
        <div style={{ color: '#cbd5e1' }}>解析品項：{s.items_parsed} 項</div>
        <div style={{ color: '#cbd5e1' }}>AI 訓練差異：{s.diff_count} 項待調整</div>
        {b.sample_id && <div style={{ color: '#cbd5e1' }}>訓練樣本編號：#{b.sample_id}</div>}
        {b.sample_id && (
          <button
            onClick={() => navigate(`/ee_clearance_training/samples/${b.sample_id}/annotate`)}
            style={{
              marginTop: 8, padding: '4px 14px', fontSize: 13, cursor: 'pointer',
              background: '#f59e0b', color: '#1a1a1a', border: 'none', borderRadius: 4,
              fontWeight: 600,
            }}
          >
            📌 文件標注
          </button>
        )}
      </div>
    );
  }
  if (b.status === 'stalled' && b.stall_info) {
    const si = b.stall_info;
    const histText = si.accuracy_history
      ? si.accuracy_history.map(v => `${(v * 100).toFixed(0)}%`).join(' → ')
      : null;
    return (
      <div style={{ ...detailStyle, background: '#1e1500', border: '1px solid #f59e0b' }}>
        <div style={{ color: '#fbbf24', fontWeight: 600, marginBottom: 6 }}>⏸ 訓練停滯（檔案解析正常，非錯誤）</div>
        <div style={{ color: '#fde68a', marginBottom: 6 }}>
          {si.message}{histText ? `（${histText}）` : ''}，系統已暫停訓練以節省 Claude API 成本。
        </div>
        <div style={{ color: '#fcd34d', marginBottom: 2 }}>建議：</div>
        <div style={{ color: '#fde68a', paddingLeft: 8 }}>• {si.suggestion.split('，或').join('\n').split('\n').map((line, i) => (
          <div key={i}>{i > 0 ? '• ' : ''}{line.trim()}</div>
        ))}</div>
      </div>
    );
  }
  if (b.status === 'failed' && b.failures && b.failures.length > 0) {
    const hasOverloaded = b.failures.some(f => f.reason_zh.includes('過載'));
    return (
      <div style={{ ...detailStyle, background: '#2a1500', border: '1px solid #dc3545' }}>
        <div style={{ color: '#f87171', fontWeight: 600, marginBottom: 6 }}>❌ 解析失敗</div>
        <div style={{ color: '#fca5a5', marginBottom: 4 }}>失敗檔案：</div>
        {b.failures.map((f, i) => (
          <div key={i} style={{ color: '#fca5a5', paddingLeft: 8, marginBottom: 4 }}>
            <span style={{ color: '#fde68a' }}>• {f.file}</span>
            <div style={{ color: '#fca5a5', paddingLeft: 10 }}>原因：{f.reason_zh}</div>
          </div>
        ))}
        {hasOverloaded && (
          <div style={{ color: '#fb923c', marginTop: 8 }}>建議：請於 5 分鐘後重新上傳此批次</div>
        )}
      </div>
    );
  }
  return null;
};

type BatchFilter = 'all' | 'in_progress' | 'completed' | 'failed';
const IN_PROGRESS = new Set(['queued', 'parsing', 'analyzing']);

const BatchStatusTab: React.FC = () => {
  const { showApiError } = useMessage();
  const [batches, setBatches] = useState<TrainingBatch[]>([]);
  const [filter, setFilter] = useState<BatchFilter>('all');
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const hasInProgress = batches.some(b => IN_PROGRESS.has(b.status));

  const fetchBatches = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listTrainingBatches();
      setBatches(data);
    } catch (err: any) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  }, [showApiError]);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  useEffect(() => {
    if (!hasInProgress) return;
    const t = setInterval(fetchBatches, 5000);
    return () => clearInterval(t);
  }, [hasInProgress, fetchBatches]);

  const toggleExpand = (batchId: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(batchId) ? next.delete(batchId) : next.add(batchId);
      return next;
    });
  };

  const canExpand = (b: TrainingBatch) =>
    (b.status === 'completed' && !!b.summary) ||
    (b.status === 'stalled' && !!b.stall_info) ||
    (b.status === 'failed' && b.failures && b.failures.length > 0);

  const filtered = batches.filter(b => {
    if (filter === 'all') return true;
    if (filter === 'in_progress') return IN_PROGRESS.has(b.status);
    if (filter === 'completed') return b.status === 'completed';
    if (filter === 'failed') return b.status === 'failed' || b.status === 'stalled';
    return true;
  });

  const filterBtns: { key: BatchFilter; label: string }[] = [
    { key: 'all',         label: '全部' },
    { key: 'in_progress', label: '進行中' },
    { key: 'completed',   label: '已完成' },
    { key: 'failed',      label: '失敗 / 停滯' },
  ];

  return (
    <div style={{ padding: '4px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {filterBtns.map(fb => (
            <button key={fb.key} onClick={() => setFilter(fb.key)}
              style={{
                padding: '4px 12px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
                border: filter === fb.key ? '1px solid #6f42c1' : '1px solid #dee2e6',
                background: filter === fb.key ? '#6f42c1' : '#fff',
                color: filter === fb.key ? '#fff' : '#495057',
                fontWeight: filter === fb.key ? 600 : 400,
              }}>
              {fb.label}
            </button>
          ))}
        </div>
        <button onClick={fetchBatches} disabled={loading}
          style={{ marginLeft: 'auto', padding: '4px 12px', fontSize: 12, borderRadius: 4,
            border: '1px solid #dee2e6', background: '#f8f9fa', cursor: 'pointer' }}>
          {loading ? '載入中…' : '🔄 重新整理'}
        </button>
        {hasInProgress && (
          <span style={{ fontSize: 11, color: '#6c757d' }}>（進行中，每 5 秒自動更新）</span>
        )}
      </div>

      {filtered.length === 0 ? (
        <div style={{ color: '#6c757d', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
          {loading ? '載入中…' : '尚無批次紀錄'}
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              {['', '狀態', '客戶', '檔案', '觸發時間', '耗時'].map(h => (
                <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#495057' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(b => {
              const meta = STATUS_META[b.status] ?? { label: b.status, color: '#6c757d', bg: '#f8f9fa' };
              const isTerminal = b.status === 'completed' || b.status === 'failed' || b.status === 'stalled';
              const dur = isTerminal
                ? fmtDuration(b.started_at, b.completed_at, b.submitted_at)
                : fmtDuration(b.started_at, null, b.submitted_at) + ' 進行中';
              const { rel, full } = fmtRelTime(b.submitted_at);
              const isExpanded = expanded.has(b.batch_id);
              const expandable = canExpand(b);
              return (
                <React.Fragment key={b.batch_id}>
                  <tr style={{ borderBottom: isExpanded ? 'none' : '1px solid #e9ecef' }}>
                    <td style={{ padding: '7px 6px 7px 10px', width: 20 }}>
                      {expandable && (
                        <button onClick={() => toggleExpand(b.batch_id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#6c757d', padding: 0 }}>
                          {isExpanded ? '▼' : '▶'}
                        </button>
                      )}
                    </td>
                    <td style={{ padding: '7px 10px' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                        color: meta.color, background: meta.bg, border: `1px solid ${meta.color}33`,
                      }}>
                        {meta.label}
                      </span>
                    </td>
                    <td style={{ padding: '7px 10px', color: '#495057' }}>
                      {b.customer_name ?? <span style={{ color: '#adb5bd' }}>（從報單自動）</span>}
                    </td>
                    <td style={{ padding: '7px 10px', color: '#495057' }}>
                      {b.file_count != null ? `${b.file_count} 個` : '—'}
                      {b.file_names && (
                        <div style={{ fontSize: 10, color: '#adb5bd', marginTop: 2 }}>
                          {b.file_names.split(',').slice(0, 2).map(f => f.trim()).join('、')}{b.file_names.split(',').length > 2 ? '…' : ''}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '7px 10px', color: '#495057', whiteSpace: 'nowrap' }}>
                      <span title={full} style={{ cursor: 'default', borderBottom: '1px dotted #adb5bd' }}>{rel}</span>
                    </td>
                    <td style={{ padding: '7px 10px', color: '#495057', whiteSpace: 'nowrap' }}>
                      {isTerminal ? dur : <span style={{ color: '#6f42c1' }}>{dur}</span>}
                    </td>
                  </tr>
                  {isExpanded && expandable && (
                    <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                      <td colSpan={6} style={{ padding: 0 }}>
                        <BatchExpandDetail b={b} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ─── TrainingAdvisorTab ───────────────────────────────────────────────────────

const TrainingAdvisorTab: React.FC<{ customers: Customer[] }> = ({ customers }) => {
  const { showApiError } = useMessage();
  const [messages, setMessages] = useState<AdvisorMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedCid, setSelectedCid] = useState<number | ''>('');
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const userMsg: AdvisorMessage = { role: 'user', content: text };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput('');
    setSending(true);
    try {
      const reply = await chatWithAdvisor(next, selectedCid ? Number(selectedCid) : undefined);
      setMessages(m => [...m, { role: 'assistant', content: reply }]);
    } catch (err: any) { showApiError(err); }
    finally { setSending(false); }
  };

  const clearChat = () => setMessages([]);

  return (
    <div style={{ maxWidth: 800, display: 'flex', flexDirection: 'column', height: 600 }}>
      {/* Header bar */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#6f42c1' }}>✨ 訓練顧問</div>
        <select value={selectedCid} onChange={e => setSelectedCid(e.target.value as any)}
          style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #ced4da', borderRadius: 4, flex: 1, maxWidth: 240 }}>
          <option value="">— 不限客戶（一般討論）—</option>
          {customers.filter(c => c.is_active).map(c => (
            <option key={c.id} value={c.id}>{c.name_zh}</option>
          ))}
        </select>
        {selectedCid && (
          <span style={{ fontSize: 11, color: '#6c757d' }}>
            Claude 會自動帶入此客戶的訓練現況
          </span>
        )}
        {messages.length > 0 && (
          <button onClick={clearChat}
            style={{ marginLeft: 'auto', padding: '3px 10px', fontSize: 11, background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 4, cursor: 'pointer', color: '#6c757d' }}>
            清除對話
          </button>
        )}
      </div>

      {/* Chat area */}
      <div style={{ flex: 1, overflowY: 'auto', background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 8, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', color: '#6c757d', fontSize: 13, marginTop: 40 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>✨</div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>訓練顧問</div>
            <div style={{ fontSize: 12, lineHeight: 1.8 }}>
              你可以問我：<br />
              「準確率為什麼停在 80% 了？」<br />
              「description_en 差異最多，應該怎麼調整品名規則？」<br />
              「訓練備忘應該怎麼寫比較有效？」<br />
              「這批客戶的 HS Code 格式有什麼特殊規則？」
            </div>
          </div>
        )}
        {messages.map((m, idx) => (
          <div key={idx} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{
              maxWidth: '80%', padding: '10px 14px', borderRadius: 10, fontSize: 13, lineHeight: 1.7,
              background: m.role === 'user' ? '#6f42c1' : '#fff',
              color: m.role === 'user' ? '#fff' : '#212529',
              border: m.role === 'assistant' ? '1px solid #dee2e6' : 'none',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              borderBottomRightRadius: m.role === 'user' ? 2 : 10,
              borderBottomLeftRadius: m.role === 'assistant' ? 2 : 10,
            }}>
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{ padding: '10px 14px', background: '#fff', border: '1px solid #dee2e6', borderRadius: 10, borderBottomLeftRadius: 2, fontSize: 13, color: '#6c757d' }}>
              Claude 思考中…
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input area */}
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexShrink: 0 }}>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="輸入問題或指示… (Enter 送出，Shift+Enter 換行)"
          rows={3}
          style={{ flex: 1, padding: '8px 12px', fontSize: 13, border: '1px solid #ced4da', borderRadius: 6, resize: 'none', fontFamily: 'inherit', lineHeight: 1.6 }}
          disabled={sending}
        />
        <button onClick={send} disabled={sending || !input.trim()}
          style={{ padding: '0 20px', fontSize: 13, fontWeight: 600, background: input.trim() && !sending ? '#6f42c1' : '#adb5bd', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', flexShrink: 0 }}>
          {sending ? '…' : '送出'}
        </button>
      </div>
    </div>
  );
};

// ─── TrainingPoolTab ──────────────────────────────────────────────────────────

const DOC_TYPE_OPTIONS = [
  { value: 'commercial_invoice', label: '商業發票' },
  { value: 'packing_list',       label: '裝箱單 PL' },
  { value: 'air_waybill',        label: '空運提單 AWB' },
  { value: 'customs_declaration', label: '實際申報報單' },
];

const TrainingPoolTab: React.FC<{ canUpdate: boolean }> = ({ canUpdate }) => {
  const { showSuccess, showError, showApiError } = useMessage();

  // ── Browser upload flow ────────────────────────────────────────────────────
  const [step, setStep] = useState<'upload' | 'preview' | 'submitted'>('upload');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [preview, setPreview] = useState<TrainingPoolPreviewResponse | null>(null);
  const [editedTypes, setEditedTypes] = useState<string[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [submitResult, setSubmitResult] = useState<{ batch_id: string; customer: string; files: number } | null>(null);

  // ── NAS Inbox ──────────────────────────────────────────────────────────────
  const [nasData, setNasData] = useState<NasInboxResponse | null>(null);
  const [nasLoading, setNasLoading] = useState(false);
  const [nasSubmitting, setNasSubmitting] = useState(false);
  const [showTrained, setShowTrained] = useState(false);
  // per-ticket edits: { [folder]: { customer_id, file_types } }
  const [nasEdits, setNasEdits] = useState<Record<string, { customer_id: number | null; file_types: Record<string, string> }>>({});
  const [nasSelected, setNasSelected] = useState<Set<string>>(new Set());

  const loadNasInbox = useCallback(async (includeTrained = false) => {
    setNasLoading(true);
    try {
      const d = await listNasInbox(includeTrained);
      setNasData(d);
      if (d.enabled && d.tickets) {
        // Init edits from suggestions
        const edits: typeof nasEdits = {};
        const sel = new Set<string>();
        d.tickets.forEach(t => {
          edits[t.folder] = {
            customer_id: t.suggested_customer_id,
            file_types: Object.fromEntries(t.files.map(f => [f.filename, f.doc_type])),
          };
          if (t.has_source && t.has_reference) sel.add(t.folder);
        });
        setNasEdits(edits);
        setNasSelected(sel);
      }
    } catch (err: any) { showApiError(err); }
    finally { setNasLoading(false); }
  }, [showApiError]);

  useEffect(() => { loadNasInbox(showTrained); }, [loadNasInbox, showTrained]); // eslint-disable-line react-hooks/exhaustive-deps

  const patchNasEdit = (folder: string, patch: Partial<{ customer_id: number | null; file_types: Record<string, string> }>) => {
    setNasEdits(prev => ({ ...prev, [folder]: { ...prev[folder], ...patch } }));
  };

  const patchNasFileType = (folder: string, filename: string, docType: string) => {
    setNasEdits(prev => ({
      ...prev,
      [folder]: { ...prev[folder], file_types: { ...prev[folder]?.file_types, [filename]: docType } },
    }));
  };

  const handleNasSubmit = async () => {
    const items: NasSubmitItem[] = Array.from(nasSelected)
      .map(folder => {
        const edit = nasEdits[folder];
        const ticket = nasData?.tickets?.find(t => t.folder === folder);
        if (!edit?.customer_id) {
          const autoName = ticket?.extracted_company_name;
          if (!autoName) return null;
          return { folder, customer_id: null, auto_create_name: autoName, file_types: edit?.file_types ?? {} };
        }
        return { folder, customer_id: edit.customer_id, file_types: edit.file_types };
      })
      .filter(Boolean) as NasSubmitItem[];

    if (!items.length) { showError('ERR020006', { field: '訓練池', detail: '請至少選取一個有效票券' }); return; }

    setNasSubmitting(true);
    try {
      const result = await submitNasInbox(items);
      showSuccess('SYS020001', { name: `${result.submitted} 個訓練樣本` });
      await loadNasInbox();
      setNasSelected(new Set());
    } catch (err: any) { showApiError(err); }
    finally { setNasSubmitting(false); }
  };

  // ── Browser upload handlers ────────────────────────────────────────────────
  const doPreview = async (files: File[]) => {
    if (!files.length) return;
    setUploading(true);
    setPreview(null);
    setSubmitResult(null);
    try {
      const result = await previewTrainingPool(files);
      setSelectedFiles(files);
      setPreview(result);
      setEditedTypes(result.files.map(f => f.doc_type));
      setSelectedCustomerId(result.suggested_customer_id);
      setStep('preview');
    } catch (err: any) { showApiError(err); }
    finally { setUploading(false); }
  };

  const doSubmit = async () => {
    if (!preview || (selectedCustomerId === null && !hasReference)) return;
    const typeSet = new Set(editedTypes);
    const hasSource = editedTypes.some(t => ['commercial_invoice', 'packing_list', 'air_waybill'].includes(t));
    if (!hasSource) { showError('ERR020006', { field: '文件類型', detail: '缺少來源文件' }); return; }
    if (!typeSet.has('customs_declaration')) { showError('ERR020006', { field: '文件類型', detail: '缺少實際申報報單' }); return; }
    setSubmitting(true);
    try {
      const result = await submitTrainingPool(selectedFiles, selectedCustomerId, editedTypes);
      setSubmitResult(result);
      setStep('submitted');
      showSuccess('SYS020001', { name: '訓練樣本建立' });
    } catch (err: any) { showApiError(err); }
    finally { setSubmitting(false); }
  };

  const reset = () => {
    setStep('upload');
    setPreview(null);
    setSelectedFiles([]);
    setEditedTypes([]);
    setSelectedCustomerId(null);
    setSubmitResult(null);
  };

  const hasSource    = editedTypes.some(t => ['commercial_invoice', 'packing_list', 'air_waybill'].includes(t));
  const hasReference = editedTypes.includes('customs_declaration');

  const fmtSize = (b: number) => b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`;

  return (
    <div style={{ maxWidth: 900 }}>

      {/* ── NAS Inbox ─────────────────────────────────────────────────────── */}
      {nasData && nasData.enabled && (
        <div style={{ marginBottom: 28, border: '1px solid #dee2e6', borderRadius: 8, overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', background: '#1e1b4b', color: '#a78bfa' }}>
            <div>
              <span style={{ fontSize: 13, fontWeight: 600 }}>📂 NAS 收件匣</span>
              <span style={{ fontSize: 11, color: '#818cf8', marginLeft: 10 }}>
                {nasData.tickets?.filter(t => !t.is_trained).length ?? 0} 票待訓練
                {nasData.tickets?.some(t => t.is_trained) && (
                  <span style={{ marginLeft: 6 }}>‧ {nasData.tickets?.filter(t => t.is_trained).length} 已訓練</span>
                )}
                <span style={{ marginLeft: 6 }}>‧ 刪除資料夾內 .trained 可重新訓練</span>
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#818cf8', cursor: 'pointer' }}>
                <input type="checkbox" checked={showTrained} onChange={e => { setShowTrained(e.target.checked); loadNasInbox(e.target.checked); }} />
                顯示已訓練
              </label>
              <button onClick={() => loadNasInbox(showTrained)} disabled={nasLoading}
                style={{ padding: '4px 12px', fontSize: 11, background: 'transparent', border: '1px solid #4f46e5', color: '#a78bfa', borderRadius: 4, cursor: 'pointer' }}>
                {nasLoading ? '掃描中…' : '🔄 重新掃描'}
              </button>
              {nasSelected.size > 0 && (
                <button onClick={handleNasSubmit} disabled={nasSubmitting}
                  style={{ padding: '4px 14px', fontSize: 11, fontWeight: 600, background: '#6f42c1', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', opacity: nasSubmitting ? 0.7 : 1 }}>
                  {nasSubmitting ? '送出中…' : `✓ 送出 ${nasSelected.size} 票 → Claude 訓練`}
                </button>
              )}
            </div>
          </div>

          {/* Ticket list */}
          {nasLoading ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#6c757d', fontSize: 12 }}>掃描收件匣…</div>
          ) : !nasData.tickets?.length ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d', fontSize: 12 }}>
              收件匣目前為空<br />
              <span style={{ fontSize: 11 }}>將報單資料夾放入 NAS 收件匣，每個資料夾 = 一票，系統自動辨識</span>
            </div>
          ) : (
            <div>
              {/* Select all */}
              <div style={{ padding: '6px 12px', background: '#f8f9fa', borderBottom: '1px solid #dee2e6', display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox"
                  checked={nasSelected.size === (nasData.tickets?.filter(t => t.has_source && t.has_reference).length ?? 0) && nasSelected.size > 0}
                  onChange={e => {
                    if (e.target.checked) {
                      setNasSelected(new Set(nasData.tickets?.filter(t => t.has_source && t.has_reference).map(t => t.folder) ?? []));
                    } else {
                      setNasSelected(new Set());
                    }
                  }} />
                <span style={{ fontSize: 11, color: '#495057' }}>全選（已完整的票）</span>
              </div>

              {nasData.tickets!.map(ticket => {
                const edit = nasEdits[ticket.folder] ?? { customer_id: ticket.suggested_customer_id, file_types: {} };
                const typeSet = new Set(Object.values(edit.file_types));
                const editHasSource = Array.from(typeSet).some(t => ['commercial_invoice', 'packing_list', 'air_waybill'].includes(t));
                const editHasRef    = typeSet.has('customs_declaration');
                const canAutoCreate = !edit.customer_id && editHasRef;
                const isReady = editHasSource && editHasRef && (!!edit.customer_id || canAutoCreate);
                const isSelected = nasSelected.has(ticket.folder);

                return (
                  <div key={ticket.folder} style={{ borderBottom: '1px solid #f1f1f1', background: isSelected ? '#f0f4ff' : undefined }}>
                    {/* Ticket row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px' }}>
                      <input type="checkbox" checked={isSelected} disabled={!isReady}
                        onChange={e => {
                          setNasSelected(prev => {
                            const n = new Set(prev);
                            if (e.target.checked) n.add(ticket.folder); else n.delete(ticket.folder);
                            return n;
                          });
                        }} />
                      {/* Folder name = declaration_no hint */}
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#212529', minWidth: 130 }}>{ticket.folder}</span>
                      {ticket.is_trained && (
                        <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: '#d1fae5', color: '#065f46', fontWeight: 600 }}>✓ 已訓練</span>
                      )}
                      {/* Validation badges */}
                      <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: editHasSource ? '#d1fae5' : '#fee2e2', color: editHasSource ? '#065f46' : '#991b1b' }}>
                        {editHasSource ? '✓' : '✗'} Invoice/AWB
                      </span>
                      <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: editHasRef ? '#d1fae5' : '#fee2e2', color: editHasRef ? '#065f46' : '#991b1b' }}>
                        {editHasRef ? '✓' : '✗'} 申報報單
                      </span>
                      {/* Customer selector */}
                      <select value={edit.customer_id ?? ''} onChange={e => patchNasEdit(ticket.folder, { customer_id: Number(e.target.value) || null })}
                        style={{ padding: '3px 6px', fontSize: 11, border: `1px solid ${canAutoCreate ? '#f59e0b' : '#ced4da'}`, borderRadius: 4, minWidth: 140 }}>
                        <option value="">— 選客戶 —</option>
                        {nasData.customers?.map(c => (
                          <option key={c.id} value={c.id}>{c.name_zh || c.name_en}</option>
                        ))}
                      </select>
                      {canAutoCreate && (
                        <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: '#fef3c7', color: '#92400e' }}>
                          📋 從報單自動建立
                        </span>
                      )}
                      <span style={{ fontSize: 10, color: '#adb5bd', marginLeft: 'auto' }}>
                        {ticket.file_count} 份 ‧ {fmtSize(ticket.size_bytes)}
                      </span>
                    </div>
                    {/* File rows */}
                    <div style={{ paddingLeft: 34, paddingBottom: 8 }}>
                      {ticket.files.map(f => (
                        <div key={f.filename} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0', fontSize: 11 }}>
                          <span style={{ color: '#6c757d', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.filename}>{f.filename}</span>
                          <select value={edit.file_types[f.filename] ?? f.doc_type}
                            onChange={e => patchNasFileType(ticket.folder, f.filename, e.target.value)}
                            style={{ padding: '2px 5px', fontSize: 10, border: '1px solid #dee2e6', borderRadius: 3 }}>
                            {DOC_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                          <span style={{ color: f.type_confidence >= 0.7 ? '#198754' : f.type_confidence >= 0.4 ? '#fd7e14' : '#dc3545' }}>
                            {Math.round(f.type_confidence * 100)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* NAS not configured hint */}
      {nasData && !nasData.enabled && (
        <div style={{ marginBottom: 20, padding: '10px 14px', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, fontSize: 11, color: '#795548' }}>
          💡 NAS 收件匣未啟用。在 docker-compose 設定 <code>TRAINING_INBOX_PATH</code> 並掛載 NAS 目錄即可啟用批次掃描模式。
        </div>
      )}

      {/* ── Browser Upload ─────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#495057', marginBottom: 10 }}>或從瀏覽器上傳（單票）</div>

        {/* Step: Upload */}
        {step === 'upload' && (
          <div>
            {uploading ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#6c757d', fontSize: 13 }}>
                🔍 辨識中，請稍候…
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <label style={{ flex: 1, minWidth: 180, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '24px 16px', background: '#fff', border: '2px dashed #a5b4fc', borderRadius: 8, cursor: canUpdate ? 'pointer' : 'not-allowed', opacity: canUpdate ? 1 : 0.5 }}>
                  <span style={{ fontSize: 28 }}>📦</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#4f46e5' }}>選 ZIP 壓縮檔</span>
                  <span style={{ fontSize: 10, color: '#6c757d', textAlign: 'center' }}>一票報單打包成 .zip</span>
                  <input type="file" accept=".zip" style={{ display: 'none' }} disabled={!canUpdate}
                    onChange={e => { const f = e.target.files?.[0]; if (f) doPreview([f]); e.target.value = ''; }} />
                </label>
                <label style={{ flex: 1, minWidth: 180, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '24px 16px', background: '#fff', border: '2px dashed #6ee7b7', borderRadius: 8, cursor: canUpdate ? 'pointer' : 'not-allowed', opacity: canUpdate ? 1 : 0.5 }}>
                  <span style={{ fontSize: 28 }}>📂</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#065f46' }}>選整個資料夾</span>
                  <span style={{ fontSize: 10, color: '#6c757d', textAlign: 'center' }}>選取資料夾內所有文件</span>
                  <input type="file" style={{ display: 'none' }} disabled={!canUpdate}
                    {...({ webkitdirectory: '', directory: '' } as any)} multiple
                    onChange={e => { const files = Array.from(e.target.files || []); if (files.length) doPreview(files); e.target.value = ''; }} />
                </label>
              </div>
            )}
          </div>
        )}

        {/* Step: Preview */}
        {step === 'preview' && preview && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: hasSource ? '#d1fae5' : '#fee2e2', color: hasSource ? '#065f46' : '#991b1b' }}>{hasSource ? '✓' : '✗'} Invoice/AWB</span>
              <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: hasReference ? '#d1fae5' : '#fee2e2', color: hasReference ? '#065f46' : '#991b1b' }}>{hasReference ? '✓' : '✗'} 申報報單</span>
            </div>
            <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#495057' }}>客戶：</span>
              <select value={selectedCustomerId ?? ''} onChange={e => setSelectedCustomerId(Number(e.target.value) || null)}
                style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #ced4da', borderRadius: 4, minWidth: 180 }}>
                <option value="">{hasReference ? '— 自動建立（從報單抽取）—' : '— 請選擇 —'}</option>
                {preview.customers.map(c => <option key={c.id} value={c.id}>{c.name_zh || c.name_en}</option>)}
              </select>
              {!selectedCustomerId && hasReference && (
                <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: '#ede9fe', color: '#6d28d9' }}>📋 從報單自動建立</span>
              )}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginBottom: 12 }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #dee2e6', fontWeight: 600 }}>檔案名稱</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #dee2e6', fontWeight: 600 }}>文件類型</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #dee2e6', fontWeight: 600 }}>信心</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #dee2e6', fontWeight: 600 }}>大小</th>
                </tr>
              </thead>
              <tbody>
                {preview.files.map((f, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f1f1f1' }}>
                    <td style={{ padding: '6px 8px', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.filename}>{f.filename}</td>
                    <td style={{ padding: '6px 8px' }}>
                      <select value={editedTypes[idx] ?? f.doc_type}
                        onChange={e => setEditedTypes(prev => { const n = [...prev]; n[idx] = e.target.value; return n; })}
                        style={{ padding: '2px 5px', fontSize: 11, border: '1px solid #ced4da', borderRadius: 3 }}>
                        {DOC_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: f.type_confidence >= 0.7 ? '#198754' : f.type_confidence >= 0.4 ? '#fd7e14' : '#dc3545' }}>
                      {Math.round(f.type_confidence * 100)}%
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: '#6c757d' }}>{fmtSize(f.file_size)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button onClick={reset} style={{ padding: '6px 14px', fontSize: 12, background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 4, cursor: 'pointer', color: '#495057' }}>← 重選</button>
              <button onClick={doSubmit} disabled={submitting || (!selectedCustomerId && !hasReference) || !hasSource || !hasReference}
                style={{ padding: '6px 18px', fontSize: 12, fontWeight: 600, background: hasSource && hasReference ? '#6f42c1' : '#adb5bd', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', opacity: submitting ? 0.7 : 1 }}>
                {submitting ? '送出中…' : '✓ 確認送出 → Claude 訓練'}
              </button>
            </div>
          </div>
        )}

        {/* Step: Submitted */}
        {step === 'submitted' && submitResult && (
          <div style={{ padding: '16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, textAlign: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#15803d', marginBottom: 4 }}>✅ 訓練樣本已送出 — Claude 後台分析中</div>
            <div style={{ fontSize: 11, color: '#6c757d', marginBottom: 10 }}>客戶：{submitResult.customer} ‧ {submitResult.files} 份 ‧ 批次 {submitResult.batch_id}</div>
            <button onClick={reset} style={{ padding: '5px 16px', fontSize: 12, background: '#6f42c1', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>＋ 繼續上傳下一票</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── CaseTrainingPanel ────────────────────────────────────────────────────────

const DOC_CHECKLIST = [
  { type: 'commercial_invoice', label: '商業發票' },
  { type: 'packing_list',       label: '裝箱單 PL' },
  { type: 'air_waybill',        label: '空運提單 AWB' },
  { type: 'customs_declaration', label: '申報報單' },
] as const;

const CaseTrainingPanel: React.FC<{
  caseData: ClearanceCase;
  docs: ClearanceDocument[];
  report: TrainingResult | null;
  analyzing: boolean;
  uploading: boolean;
  polling: boolean;
  docPolling: boolean;
  canUpdate: boolean;
  onUploadDecl: (file: File) => void;
  onUploadBundle: (file: File, docType?: string) => void;
  onRetryParse: () => void;
  onAnalyze: () => void;
  onVerify: () => void;
}> = ({ caseData, docs, report, analyzing, uploading, polling, docPolling, canUpdate, onUploadDecl, onUploadBundle, onRetryParse, onAnalyze, onVerify }) => {
  const [bundleDocType, setBundleDocType] = useState<string>('');

  const docByType = (type: string) => docs.filter(d => d.doc_type === type);
  const custDecl = docs.find(d => d.doc_type === 'customs_declaration');
  const hasInvoice = docs.some(d => d.doc_type === 'commercial_invoice' && d.parse_status === 'done');
  const canAnalyze = hasInvoice && custDecl?.parse_status === 'done';
  const hasFailed = docs.some(d => d.parse_status === 'failed');

  const statusIcon = (typeDocs: ClearanceDocument[]) => {
    if (!typeDocs.length) return { icon: '✗', color: '#adb5bd' };
    const done = typeDocs.some(d => d.parse_status === 'done');
    const running = typeDocs.some(d => ['pending', 'parsing', 'running'].includes(d.parse_status));
    const failed = typeDocs.some(d => d.parse_status === 'failed');
    if (done) return { icon: '✓', color: '#198754' };
    if (running) return { icon: '⏳', color: '#fd7e14' };
    if (failed) return { icon: '✗', color: '#dc3545' };
    return { icon: '✗', color: '#adb5bd' };
  };

  return (
    <div style={{ padding: '16px 20px' }}>
      {/* Document checklist */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 14, fontSize: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {DOC_CHECKLIST.map(({ type, label }) => {
          const typeDocs = docByType(type);
          const { icon, color } = statusIcon(typeDocs);
          return (
            <span key={type} style={{ color, display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontWeight: 700 }}>{icon}</span>
              {label}
              {typeDocs.length > 1 && <span style={{ color: '#6c757d' }}>×{typeDocs.length}</span>}
            </span>
          );
        })}
        {docPolling && (
          <span style={{ color: '#0dcaf0', fontSize: 11 }}>🔄 解析中，每 5 秒自動更新…</span>
        )}
      </div>

      {/* Bundle upload (Invoice / PL / AWB / ZIP) */}
      {canUpdate && !docPolling && (
        <div style={{ background: '#f0f4ff', border: '1px solid #c7d2fe', borderRadius: 6, padding: '10px 14px', marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#3730a3', marginBottom: 8 }}>上傳訓練文件</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={bundleDocType} onChange={e => setBundleDocType(e.target.value)}
              style={{ padding: '5px 8px', fontSize: 12, border: '1px solid #a5b4fc', borderRadius: 4, background: '#fff' }}>
              <option value="">ZIP（自動辨識）</option>
              <option value="commercial_invoice">商業發票</option>
              <option value="packing_list">裝箱單 PL</option>
              <option value="air_waybill">空運提單 AWB</option>
            </select>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', fontSize: 12, background: '#4f46e5', color: '#fff', borderRadius: 4, cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.7 : 1 }}>
              {uploading ? '上傳中...' : '📎 選擇檔案'}
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.tiff,.zip" style={{ display: 'none' }}
                disabled={uploading}
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const isZip = f.name.toLowerCase().endsWith('.zip');
                  onUploadBundle(f, isZip ? undefined : (bundleDocType || 'commercial_invoice'));
                  e.target.value = '';
                }} />
            </label>
            <span style={{ fontSize: 11, color: '#6366f1' }}>
              ZIP 自動解壓辨識 Invoice / PL / AWB
            </span>
          </div>
        </div>
      )}

      {/* Upload customs_declaration */}
      {canUpdate && !custDecl && (
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', fontSize: 12, background: '#856404', color: '#fff', borderRadius: 4, cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.7 : 1 }}>
            {uploading ? '上傳中...' : '📚 上傳實際申報報單'}
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; if (f) onUploadDecl(f); e.target.value = ''; }}
              disabled={uploading} />
          </label>
          <span style={{ marginLeft: 10, fontSize: 11, color: '#856404' }}>海關核准的進口報單 PDF</span>
        </div>
      )}

      {/* Retry parse for failed docs */}
      {canUpdate && hasFailed && !docPolling && (
        <div style={{ marginBottom: 14 }}>
          <button
            onClick={onRetryParse}
            style={{ padding: '5px 14px', fontSize: 12, background: '#dc3545', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
            🔄 重試解析（有文件失敗）
          </button>
        </div>
      )}

      {/* Analyze button */}
      {canUpdate && canAnalyze && !report && !polling && (
        <div style={{ marginBottom: 14 }}>
          <button
            style={{ padding: '6px 16px', fontSize: 13, background: '#6f42c1', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
            onClick={onAnalyze} disabled={analyzing}>
            {analyzing ? '送出中...' : '🧠 Claude 配對分析 → 更新客戶解析規格'}
          </button>
        </div>
      )}

      {/* Loading ticker while Claude works */}
      {polling && !report && (
        <div style={{ ...forgePanelStyle, marginTop: 0, marginBottom: 14 }}>
          <div style={forgeHeaderStyle}>
            <span>✨ xcraftforge — Claude 正在打磨...</span>
            <span style={{ fontSize: 11, opacity: 0.7 }}>分析比對中，請稍候</span>
          </div>
          <div style={forgeTickerWrapStyle}>
            <div style={{ ...forgeTickerStyle, animation: 'forgeScroll 4s linear infinite' }}>
              <style>{`@keyframes forgeScroll { 0% { transform: translateY(0); } 100% { transform: translateY(-50%); } }`}</style>
              {['讀取商業發票解析結果…', '讀取實際申報報單…', '比對欄位差異…',
                '分析品名對應規則…', '建立客戶解析規格…', '產生學習摘要…',
                '讀取商業發票解析結果…', '讀取實際申報報單…', '比對欄位差異…',
                '分析品名對應規則…', '建立客戶解析規格…', '產生學習摘要…'].map((line, i) => (
                <div key={i} style={forgeLineStyle}>
                  <span style={{ color: '#a78bfa' }}>›</span> {line}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Forge report */}
      {report && (
        <ForgeReport
          result={report}
          customerId={caseData.customer_id}
          onVerify={onVerify}
          onAnalyze={canUpdate ? onAnalyze : undefined}
        />
      )}
    </div>
  );
};

// ─── ForgeReport ──────────────────────────────────────────────────────────────

// 進口報單官方欄位名稱（MIG / NX5105 進口報單格式）
const DECL_FIELD_LABEL: Record<string, string> = {
  description_en:  '貨物名稱、商標(牌名)及規格等（第35欄）',
  origin_country:  '生產國別（第36欄）',
  hs_code:         '稅則號別（第38欄）',
  hs_stat:         '統計號別（第38欄）',
  hs_check:        '檢查號碼（第38欄）',
  trade_condition: '條件、幣別（第39欄）',
  amount_usd:      '金額—外幣（第39欄）',
  net_weight_kg:   '淨重 公斤（第40欄）',
  qty:             '數量（第41欄）',
  unit:            '單位（第41欄）',
  stat_qty:        '統計數量（第42欄）',
  stat_unit:       '統計單位（第42欄）',
  amount_twd:      '完稅價格（第43欄）',
  duty_rate:       '進口稅率（第44欄）',
  tax_method_code: '納稅辦法（第45欄）',
  gross_weight_kg: '毛重 公斤（申報表頭）',
  total_packages:  '件數（申報表頭）',
  unit_price_usd:  '單價',
};

// Invoice / Packing List 來源欄位名稱
const INV_FIELD_LABEL: Record<string, string> = {
  description:    '貨物名稱',
  part_no:        '料號 (P/N)',
  device:         '商標/牌名',
  lot_no:         '批號 (LOT)',
  model_no:       '型號',
  product_code:   '產品代碼',
};

const DEFAULT_RULE_COMPONENTS: DescriptionRuleComponent[] = [
  { field: 'part_no',      prefix: 'P/N:', enabled: true },
  { field: 'description',  prefix: '',     enabled: true },
  { field: 'lot_no',       prefix: 'LOT:', enabled: true },
  { field: 'device',       prefix: '',     enabled: true },
  { field: 'model_no',     prefix: '',     enabled: false },
  { field: 'product_code', prefix: '',     enabled: false },
];

const DIFF_TYPE_META: Record<string, { label: string; color: string; bg: string }> = {
  content_diff: { label: '修正', color: '#fd7e14', bg: '#3a1f00' },
  missing:      { label: '缺漏', color: '#f87171', bg: '#3b0a0a' },
  extra:        { label: '多餘', color: '#94a3b8', bg: '#1e293b' },
};

const REASON_OPTIONS: { value: DiffReasonType; label: string; hint: string }[] = [
  { value: 'transform_rule', label: '轉換規則', hint: '有固定計算/轉換邏輯（如：USD × 匯率 = TWD）' },
  { value: 'source_mapping' as DiffReasonType, label: '來源對應', hint: '欄位名稱不同但資料同源（如：invoice unit DIE → 申報單位 PCE）' },
  { value: 'format_rule',    label: '稅則補完', hint: 'Invoice 國際 HS Code（6~8碼）→ 台灣國內稅則號列（11碼），需查關稅署稅則補足統計碼與檢查碼' },
  { value: 'manual_fill',    label: '人工補填', hint: '來源文件無此資料，必須人工查找輸入' },
  { value: 'correction',     label: '申報修正', hint: '繕打時依實況人工判斷修正' },
  { value: 'ai_error',       label: 'AI 錯誤',  hint: '應可從來源推導，AI 未正確處理' },
];

const ForgeReport: React.FC<{
  result: TrainingResult;
  customerId?: number;
  onVerify: () => void;
  onAnalyze?: () => void;
}> = ({ result, customerId, onVerify, onAnalyze }) => {
  const { showApiError, showSuccess } = useMessage();
  const { stats, field_diffs, ai_summary, customer_sample_count, is_verified, analyzed_at } = result;

  const initAnnotations = (): Record<string, DiffAnnotation> => {
    const map: Record<string, DiffAnnotation> = {};
    (result.diff_annotations || []).forEach(a => {
      map[`${a.item_sno}__${a.field}`] = a;
    });
    return map;
  };

  const [annotations, setAnnotations] = useState<Record<string, DiffAnnotation>>(initAnnotations);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showDiffTable, setShowDiffTable] = useState(true);
  const [showItemComparison, setShowItemComparison] = useState(true);

  // ── 品名規則編輯器 ─────────────────────────────────────────────────────────
  const [showRuleEditor, setShowRuleEditor] = useState(false);
  const [ruleComponents, setRuleComponents] = useState<DescriptionRuleComponent[]>(DEFAULT_RULE_COMPONENTS);
  const [ruleSeparator, setRuleSeparator] = useState(' ');
  const [ruleSaving, setRuleSaving] = useState(false);

  useEffect(() => {
    if (!result.customer_id) return;
    getCustomerParseProfile(result.customer_id).then(p => {
      const rule: ItemDescriptionRule | undefined = (p.parse_profile as any)?.item_description_rule;
      if (rule?.components?.length) {
        setRuleComponents(rule.components);
        setRuleSeparator(rule.separator ?? ' ');
      }
    }).catch(() => {});
  }, [result.customer_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveRule = async () => {
    setRuleSaving(true);
    try {
      const sampleItem = result.item_comparison?.[0];
      const exampleItem: Record<string, string> = sampleItem
        ? { description: sampleItem.inv_description, ...sampleItem.inv_extra }
        : {};
      const exampleOutput = sampleItem ? previewDescription(sampleItem) : '';
      await saveItemDescriptionRule(result.customer_id, {
        components: ruleComponents,
        separator: ruleSeparator,
        example_item: Object.keys(exampleItem).length ? exampleItem : undefined,
        example_output: exampleOutput || undefined,
      });
      showSuccess('SYS020002', { name: '品名規則' });
    } catch (err: any) { showApiError(err); }
    finally { setRuleSaving(false); }
  };

  const previewDescription = (item: ItemComparison): string => {
    const src: Record<string, string> = {
      description: item.inv_description,
      ...item.inv_extra,
    };
    const parts = ruleComponents
      .filter(c => c.enabled && src[c.field])
      .map(c => `${c.prefix}${src[c.field]}`);
    return parts.join(ruleSeparator) || '（無可用資料）';
  };

  const moveComp = (idx: number, dir: -1 | 1) => {
    setRuleComponents(prev => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const patchComp = (idx: number, patch: Partial<DescriptionRuleComponent>) => {
    setRuleComponents(prev => prev.map((c, i) => i === idx ? { ...c, ...patch } : c));
  };

  const autoDetectRule = (item: ItemComparison) => {
    const actual = item.actual_description;
    const actualUpper = actual.toUpperCase();
    const src: Record<string, string> = { description: item.inv_description, ...item.inv_extra };
    const PREFIXES = ['P/N:', 'LOT:', 'S/N:', 'DEVICE:', 'MODEL:', 'PART:', 'PN:', 'REV:'];
    const ALL_FIELDS = ['part_no', 'description', 'device', 'lot_no', 'model_no', 'product_code'];

    const found: Array<{ field: string; prefix: string; pos: number }> = [];
    const notFound: DescriptionRuleComponent[] = [];

    for (const field of ALL_FIELDS) {
      const val = src[field];
      if (!val) { notFound.push({ field, prefix: '', enabled: false }); continue; }
      const idx = actualUpper.indexOf(val.toUpperCase());
      if (idx < 0) { notFound.push({ field, prefix: '', enabled: false }); continue; }
      const before = actualUpper.substring(0, idx);
      let prefix = '';
      for (const p of PREFIXES) {
        if (before.endsWith(p)) { prefix = p; break; }
      }
      found.push({ field, prefix, pos: idx });
    }

    found.sort((a, b) => a.pos - b.pos);
    setRuleComponents([
      ...found.map(f => ({ field: f.field, prefix: f.prefix, enabled: true })),
      ...notFound,
    ]);
  };

  const diffKey = (sno: number, field: string) => `${sno}__${field}`;

  const handleAnnotationChange = (sno: number, field: string, patch: Partial<DiffAnnotation>) => {
    const key = diffKey(sno, field);
    setAnnotations(prev => {
      const base: DiffAnnotation = prev[key] ?? { item_sno: sno, field, reason_type: '', explanation: '' };
      return { ...prev, [key]: { ...base, ...patch } };
    });
  };

  const handleSaveAnnotations = async () => {
    setSaving(true);
    try {
      const list = Object.values(annotations).filter(a => a.reason_type || a.explanation);
      await saveDiffAnnotations(result.sample_id, list);
      showSuccess('SYS020002', { name: '差異說明' });
    } catch (err: any) { showApiError(err); }
    finally { setSaving(false); }
  };

  const annotatedCount = Object.values(annotations).filter(a => a.reason_type || a.explanation).length;

  return (
    <div style={{ ...forgePanelStyle, marginTop: 16 }}>
      <style>{`@keyframes forgeScroll { 0% { transform:translateY(0); } 100% { transform:translateY(-50%); } }`}</style>

      {/* Header */}
      <div style={forgeHeaderStyle}>
        <span>✨ xcraftforge — Claude 打磨報告</span>
        <span style={{ fontSize: 11, opacity: 0.7 }}>
          第 {customer_sample_count} 次學習 ‧ {formatLocal(analyzed_at)}
          {is_verified && <span style={{ marginLeft: 8, color: '#6ee7b7' }}>✓ 已確認</span>}
        </span>
      </div>

      {/* AI Summary */}
      {ai_summary && (
        <div style={{ padding: '8px 14px', fontSize: 12, color: '#c4b5fd', borderBottom: '1px solid #312e81', fontStyle: 'italic' }}>
          💡 {ai_summary}
        </div>
      )}

      {/* Stats bar */}
      <div style={{ display: 'flex', gap: 16, padding: '8px 14px', alignItems: 'center', borderBottom: '1px solid #312e81', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: '#6ee7b7' }}>✓ 吻合 {stats.match}</span>
        <span style={{ fontSize: 11, color: '#fd7e14' }}>⚡ 修正 {stats.diff}</span>
        <span style={{ fontSize: 11, color: '#f87171' }}>✗ 缺漏 {stats.missing}</span>
        {stats.extra > 0 && <span style={{ fontSize: 11, color: '#94a3b8' }}>＋ 多餘 {stats.extra}</span>}
        <span style={{ fontSize: 11, color: '#818cf8', marginLeft: 'auto' }}>
          AI 準確率 {stats.accuracy_pct}%
          {(stats.human_only_excluded ?? 0) > 0 && (
            <span style={{ fontSize: 10, color: '#64748b', marginLeft: 6 }}>
              （已排除 {stats.human_only_excluded} 個人工欄位）
            </span>
          )}
        </span>
        <button onClick={() => setShowDiffTable(v => !v)}
          style={{ fontSize: 11, background: 'none', border: '1px solid #312e81', color: '#a78bfa', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}>
          {showDiffTable ? '▲ 收起明細' : '▼ 展開明細'}
        </button>
      </div>

      {/* Diff detail table */}
      {showDiffTable && field_diffs.length > 0 && (
        <div style={{ maxHeight: 480, overflowY: 'auto' }}>
          {field_diffs.map((d, idx) => {
            const key = diffKey(d.item_sno, d.field);
            const meta = DIFF_TYPE_META[d.diff_type] || DIFF_TYPE_META.extra;
            const ann = annotations[key];
            const isExpanded = expandedKey === key;
            const hasAnnotation = !!(ann?.reason_type || ann?.explanation);

            return (
              <div key={key} style={{ borderBottom: '1px solid #1e1b4b', background: isExpanded ? '#1a1730' : undefined }}>
                {/* Diff row */}
                <div
                  onClick={() => setExpandedKey(isExpanded ? null : key)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 14px', cursor: 'pointer', fontSize: 12 }}
                >
                  <span style={{ background: meta.bg, color: meta.color, fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3, minWidth: 28, textAlign: 'center', flexShrink: 0 }}>
                    {meta.label}
                  </span>
                  <span style={{ color: '#94a3b8', minWidth: 32, flexShrink: 0 }}>#{d.item_sno}</span>
                  <span style={{ color: '#e2e8f0', minWidth: 160, flexShrink: 0, fontSize: 11 }}>{DECL_FIELD_LABEL[d.field] ?? d.field}</span>
                  {d.diff_type === 'content_diff' && (
                    <span style={{ color: '#94a3b8', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <span style={{ color: '#f87171' }}>「{d.generated}」</span>
                      <span style={{ color: '#64748b' }}> → </span>
                      <span style={{ color: '#6ee7b7' }}>「{d.reference}」</span>
                    </span>
                  )}
                  {d.diff_type === 'missing' && (
                    <span style={{ color: '#94a3b8', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <span style={{ color: '#64748b' }}>AI 未填 → </span>
                      <span style={{ color: '#6ee7b7' }}>「{d.reference}」</span>
                    </span>
                  )}
                  {d.diff_type === 'extra' && (
                    <span style={{ color: '#94a3b8', flex: 1 }}>
                      <span style={{ color: '#f87171' }}>「{d.generated}」</span>
                      <span style={{ color: '#64748b' }}> 報單未填此欄</span>
                    </span>
                  )}
                  <span style={{ flexShrink: 0, fontSize: 10 }}>
                    {hasAnnotation
                      ? <span style={{ color: '#6ee7b7' }}>✎ 已說明</span>
                      : <span style={{ color: '#475569' }}>✎ 補說明</span>}
                  </span>
                  <span style={{ color: '#475569', fontSize: 10 }}>{isExpanded ? '▲' : '▼'}</span>
                </div>

                {/* Annotation panel */}
                {isExpanded && (
                  <div style={{ padding: '10px 14px 14px 14px', background: '#13111f', borderTop: '1px solid #1e1b4b' }}>
                    <div style={{ fontSize: 11, color: '#818cf8', marginBottom: 8 }}>差異原因說明（幫助 Claude 學習此客戶的申報規則）</div>

                    {/* Reason type */}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                      {REASON_OPTIONS.map(r => (
                        <button key={r.value} onClick={() => handleAnnotationChange(d.item_sno, d.field, { reason_type: r.value })}
                          title={r.hint}
                          style={{
                            padding: '3px 10px', fontSize: 11, borderRadius: 12, cursor: 'pointer', border: '1px solid',
                            background: ann?.reason_type === r.value ? '#4f46e5' : 'transparent',
                            borderColor: ann?.reason_type === r.value ? '#6366f1' : '#312e81',
                            color: ann?.reason_type === r.value ? '#fff' : '#94a3b8',
                          }}>
                          {r.label}
                        </button>
                      ))}
                    </div>

                    {/* Hint text */}
                    {ann?.reason_type && (
                      <div style={{ fontSize: 10, color: '#6366f1', marginBottom: 8, fontStyle: 'italic' }}>
                        {REASON_OPTIONS.find(r => r.value === ann.reason_type)?.hint}
                      </div>
                    )}

                    {/* Explanation textarea */}
                    <textarea
                      value={ann?.explanation || ''}
                      onChange={e => handleAnnotationChange(d.item_sno, d.field, { explanation: e.target.value })}
                      placeholder={
                        ann?.reason_type === 'transform_rule' ? '例：invoice 金額（USD）× 申報匯率 = amount_twd（TWD），匯率來自案件設定' :
                        ann?.reason_type === 'manual_fill' ? '例：net_weight_kg 需查倉單或實際秤重，invoice 無此資訊' :
                        ann?.reason_type === 'format_rule' ? '例：Invoice HS Code 8542.39（6碼）→ 申報稅則 8542.39.00.12-0（11碼），後段需查關稅署稅則確認' :
                        '請說明此差異的原因或轉換邏輯…'
                      }
                      rows={3}
                      style={{
                        width: '100%', boxSizing: 'border-box', background: '#1e1b4b', border: '1px solid #312e81',
                        borderRadius: 4, color: '#e2e8f0', fontSize: 12, padding: '6px 8px', resize: 'vertical',
                        fontFamily: 'inherit',
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Item Comparison Panel */}
      {result.item_comparison && result.item_comparison.length > 0 && (
        <>
          <div
            onClick={() => setShowItemComparison(v => !v)}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 14px', borderTop: '1px solid #312e81', cursor: 'pointer', background: '#110f1e' }}>
            <span style={{ fontSize: 12, color: '#a78bfa', fontWeight: 600 }}>
              第35欄貨物名稱對照（{result.item_comparison.length} 項）
              <span style={{ fontSize: 10, color: '#64748b', marginLeft: 8, fontWeight: 400 }}>
                Invoice 貨物名稱/商標(牌名)/規格 → 進口報單第35欄
              </span>
            </span>
            <span style={{ fontSize: 10, color: '#475569' }}>{showItemComparison ? '▲ 收起' : '▼ 展開'}</span>
          </div>
          {showItemComparison && (
            <div style={{ overflowX: 'auto', maxHeight: 380, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <colgroup>
                  <col style={{ width: 34 }} />
                  <col style={{ width: '24%' }} />
                  <col style={{ width: '21%' }} />
                  <col style={{ width: '21%' }} />
                  <col style={{ width: '24%' }} />
                  <col style={{ width: 36 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={cmpThStyle}>#</th>
                    <th style={cmpThStyle}>第35欄：貨物名稱/商標/規格（報單值）</th>
                    <th style={cmpThStyle}>貨物名稱（Invoice）</th>
                    <th style={cmpThStyle}>商標/規格等附加欄位</th>
                    <th style={cmpThStyle}>AI 合成貨名</th>
                    <th style={cmpThStyle}>符合</th>
                  </tr>
                </thead>
                <tbody>
                  {result.item_comparison.map((item: ItemComparison) => {
                    const rowBg = item.match === true ? '#0a2318' : item.match === false ? '#2a1500' : '#13111f';
                    return (
                      <tr key={item.sno} style={{ background: rowBg, borderBottom: '1px solid #1e1b4b' }}>
                        <td style={{ ...cmpTdStyle, color: '#64748b', textAlign: 'center' }}>{item.sno}</td>
                        <td style={{ ...cmpTdStyle, color: '#6ee7b7' }}>
                          {item.actual_description || <span style={{ color: '#475569' }}>—</span>}
                        </td>
                        <td style={{ ...cmpTdStyle, color: '#e2e8f0' }}>
                          {item.inv_description
                            ? item.inv_description
                            : <span style={{ color: '#ef4444', fontSize: 10 }}>未擷取</span>
                          }
                        </td>
                        <td style={cmpTdStyle}>
                          {Object.keys(item.inv_extra).length > 0
                            ? Object.entries(item.inv_extra).map(([k, v]) => (
                                <div key={k} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  <span style={{ color: '#818cf8' }}>{INV_FIELD_LABEL[k] ?? k}</span>: <span style={{ color: '#c4b5fd' }}>{String(v)}</span>
                                </div>
                              ))
                            : <span style={{ color: '#475569' }}>—</span>
                          }
                        </td>
                        <td style={{ ...cmpTdStyle, color: '#94a3b8' }}>
                          {item.ai_description
                            ? item.ai_description
                            : <span style={{ color: '#475569', fontSize: 10 }}>AI 未填</span>
                          }
                        </td>
                        <td style={{ ...cmpTdStyle, textAlign: 'center' }}>
                          {item.match === true  && <span style={{ color: '#6ee7b7', fontSize: 14 }}>✓</span>}
                          {item.match === false && <span style={{ color: '#f87171', fontSize: 14 }}>✗</span>}
                          {item.match === null  && <span style={{ color: '#475569' }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {/* Rule editor toggle */}
              <div
                onClick={() => setShowRuleEditor(v => !v)}
                style={{ padding: '7px 14px', borderTop: '1px solid #1e1b4b', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#0f0e1a' }}>
                <span style={{ fontSize: 11, color: '#818cf8' }}>
                  ⚙ 第35欄貨物名稱合成規則設定
                  <span style={{ marginLeft: 8, fontSize: 10, color: '#475569' }}>（定義報單第35欄如何從 Invoice 來源欄位組合，學習後 Claude 直接合成）</span>
                </span>
                <span style={{ fontSize: 10, color: '#475569' }}>{showRuleEditor ? '▲' : '▼'}</span>
              </div>

              {/* Rule editor */}
              {showRuleEditor && (() => {
                const sampleItem = result.item_comparison?.[0];
                const sampleSrc: Record<string, string> = sampleItem
                  ? { description: sampleItem.inv_description, ...sampleItem.inv_extra }
                  : {};
                const preview = sampleItem ? previewDescription(sampleItem) : '';
                const previewMatchesActual = sampleItem
                  ? preview.trim().toUpperCase() === sampleItem.actual_description.trim().toUpperCase()
                  : false;
                return (
                  <div style={{ padding: '12px 14px', background: '#0c0b17', borderTop: '1px solid #1e1b4b' }}>

                    {/* Target declaration description */}
                    {sampleItem && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 10, color: '#64748b', marginBottom: 4 }}>
                          目標：第35欄貨物名稱（報單實際值 — 第 {sampleItem.sno} 項）
                        </div>
                        <div style={{ padding: '6px 10px', background: '#1a1730', borderRadius: 4, borderLeft: '3px solid #6ee7b7', fontFamily: 'monospace', fontSize: 12, color: '#6ee7b7', wordBreak: 'break-all', lineHeight: 1.6 }}>
                          {sampleItem.actual_description || <span style={{ color: '#475569' }}>（無申報品名）</span>}
                        </div>
                        <button
                          onClick={() => autoDetectRule(sampleItem)}
                          style={{ marginTop: 6, padding: '3px 12px', fontSize: 11, background: '#1e293b', color: '#a78bfa', border: '1px solid #4f46e5', borderRadius: 4, cursor: 'pointer' }}>
                          🔍 自動偵測對應欄位
                        </button>
                        <span style={{ marginLeft: 8, fontSize: 10, color: '#475569' }}>
                          系統比對各 Invoice 欄位值在申報品名中的位置，自動填入規則
                        </span>
                      </div>
                    )}

                    {/* Field chips with actual values */}
                    <div style={{ fontSize: 10, color: '#818cf8', marginBottom: 8 }}>
                      Invoice 來源欄位 → 組合成報單第35欄貨物名稱（勾選啟用、設前綴、調順序）：
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}>
                      {ruleComponents.map((comp, idx) => {
                        const val = sampleSrc[comp.field];
                        return (
                          <div key={comp.field} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', background: comp.enabled ? '#1a1730' : '#0f0e1a', borderRadius: 4, border: `1px solid ${comp.enabled ? '#312e81' : '#1e1b4b'}` }}>
                            <input type="checkbox" checked={comp.enabled} onChange={e => patchComp(idx, { enabled: e.target.checked })}
                              style={{ cursor: 'pointer', flexShrink: 0 }} />
                            <span style={{ color: comp.enabled ? '#c4b5fd' : '#475569', fontSize: 11, minWidth: 110, flexShrink: 0 }}>{INV_FIELD_LABEL[comp.field] ?? comp.field}</span>
                            <span style={{ color: val ? (comp.enabled ? '#e2e8f0' : '#475569') : '#374151', fontSize: 11, fontFamily: 'monospace', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {val ? `"${val}"` : '（此筆無資料）'}
                            </span>
                            <span style={{ color: '#64748b', fontSize: 10, flexShrink: 0 }}>前綴</span>
                            <input
                              value={comp.prefix}
                              onChange={e => patchComp(idx, { prefix: e.target.value })}
                              placeholder="無"
                              style={{ width: 64, padding: '2px 6px', fontSize: 11, background: '#1e1b4b', border: '1px solid #312e81', borderRadius: 3, color: '#e2e8f0', fontFamily: 'monospace', textAlign: 'center' }}
                              disabled={!comp.enabled}
                            />
                            <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                              <button onClick={() => moveComp(idx, -1)} disabled={idx === 0}
                                style={{ padding: '1px 5px', fontSize: 10, background: 'transparent', border: '1px solid #312e81', color: '#818cf8', borderRadius: 3, cursor: 'pointer', opacity: idx === 0 ? 0.25 : 1 }}>↑</button>
                              <button onClick={() => moveComp(idx, 1)} disabled={idx === ruleComponents.length - 1}
                                style={{ padding: '1px 5px', fontSize: 10, background: 'transparent', border: '1px solid #312e81', color: '#818cf8', borderRadius: 3, cursor: 'pointer', opacity: idx === ruleComponents.length - 1 ? 0.25 : 1 }}>↓</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Live preview with match indicator */}
                    {sampleItem && (
                      <div style={{ marginBottom: 14, padding: '7px 10px', background: '#1e1b4b', borderRadius: 4, borderLeft: `3px solid ${previewMatchesActual ? '#6ee7b7' : '#f59e0b'}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <span style={{ fontSize: 10, color: '#818cf8' }}>組合結果預覽</span>
                          <span style={{ fontSize: 10, fontWeight: 600, color: previewMatchesActual ? '#6ee7b7' : '#f59e0b' }}>
                            {previewMatchesActual ? '✓ 與申報品名吻合' : '≠ 尚未吻合，請調整'}
                          </span>
                        </div>
                        <div style={{ fontFamily: 'monospace', fontSize: 11, color: previewMatchesActual ? '#6ee7b7' : '#fbbf24', wordBreak: 'break-all' }}>
                          {preview || '（無輸出）'}
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <button onClick={handleSaveRule} disabled={ruleSaving}
                        style={{ padding: '4px 14px', fontSize: 11, background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', opacity: ruleSaving ? 0.7 : 1 }}>
                        {ruleSaving ? '儲存中…' : '💾 儲存規則'}
                      </button>
                      {onAnalyze && (
                        <button onClick={onAnalyze}
                          style={{ padding: '4px 14px', fontSize: 11, background: 'transparent', color: '#a78bfa', border: '1px solid #4f46e5', borderRadius: 4, cursor: 'pointer' }}>
                          🔁 套用並重新分析
                        </button>
                      )}
                      <span style={{ fontSize: 10, color: '#475569' }}>儲存後重新產生報單即套用</span>
                    </div>
                  </div>
                );
              })()}

              <div style={{ padding: '6px 14px', fontSize: 10, color: '#475569', borderTop: '1px solid #1e1b4b' }}>
                ℹ 進口報單第35欄（貨物名稱、商標(牌名)及規格等）為固定申報欄位；貨物名稱/商標/牌名/規格等為 Invoice / PL 可變來源欄位。「未擷取」表示解析器未識別該欄位，設定規則後重新分析可提升識別率。
              </div>
            </div>
          )}
        </>
      )}

      {/* Footer */}
      <div style={{ padding: '10px 14px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', borderTop: '1px solid #312e81' }}>
        {annotatedCount > 0 && (
          <span style={{ fontSize: 11, color: '#6ee7b7' }}>✎ {annotatedCount} 條已說明</span>
        )}
        <button onClick={handleSaveAnnotations} disabled={saving}
          style={{ padding: '4px 12px', fontSize: 11, background: '#1e40af', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving ? '儲存中…' : '💾 儲存說明'}
        </button>
        {onAnalyze && (
          <button onClick={onAnalyze}
            style={{ padding: '4px 12px', fontSize: 11, background: 'transparent', color: '#a78bfa', border: '1px solid #4f46e5', borderRadius: 4, cursor: 'pointer' }}>
            🔁 重新分析
          </button>
        )}
        {!is_verified && (
          <button onClick={onVerify}
            style={{ padding: '4px 12px', fontSize: 11, background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
            ✓ 確認納入訓練
          </button>
        )}
        <span style={{ fontSize: 11, color: '#a78bfa', marginLeft: 'auto' }}>
          AI 準確率 {stats.accuracy_pct}%
          {(stats.human_only_excluded ?? 0) > 0 && (
            <span style={{ fontSize: 10, color: '#64748b', marginLeft: 6 }}>
              （已排除 {stats.human_only_excluded} 個人工欄位）
            </span>
          )}
        </span>
        {customerId && (
          <a href={trainingExportUrl(customerId)} target="_blank" rel="noreferrer"
            style={{ fontSize: 11, color: '#818cf8' }}>⬇ JSONL</a>
        )}
      </div>
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const cmpThStyle: React.CSSProperties = {
  padding: '5px 8px', color: '#818cf8', fontWeight: 600, textAlign: 'left',
  fontSize: 10, borderBottom: '1px solid #312e81', position: 'sticky', top: 0,
  background: '#1e1b4b', whiteSpace: 'nowrap',
};

const cmpTdStyle: React.CSSProperties = {
  padding: '5px 8px', verticalAlign: 'top', lineHeight: 1.5,
  overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: 'break-word',
};

const forgePanelStyle: React.CSSProperties = {
  marginTop: 16, background: '#0f0e1a', border: '1px solid #312e81',
  borderRadius: 8, overflow: 'hidden', fontFamily: 'monospace',
};

const forgeHeaderStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '8px 12px', background: 'linear-gradient(90deg, #1e1b4b, #312e81)',
  color: '#a78bfa', fontSize: 12, fontWeight: 600,
};

const forgeTickerWrapStyle: React.CSSProperties = {
  height: 110, overflow: 'hidden', position: 'relative',
};

const forgeTickerStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', willChange: 'transform',
};

const forgeLineStyle: React.CSSProperties = {
  display: 'flex', gap: 8, alignItems: 'baseline',
  padding: '3px 12px', fontSize: 11, color: '#e2e8f0',
  borderBottom: '1px solid #1e1b4b', whiteSpace: 'nowrap',
};

export default ClearanceTrainingCenterPage;